import React, { Fragment, useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import "../../../assets/css/DataTableDemo.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "../../../assets/css/PrimeGrid.css";

import { AlertContext } from "../../../context/AlertContext";
import { AuthContext } from "../../../context/AuthContext";
import { ConfiguracionContext } from "../../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../../theme/brand";
import Spinner from "../Spinner";
import Barra from "../Barra";
import CabeceraPantalla from "../common/CabeceraPantalla";

export default function Accesos() {
  const [globalFilter, setGlobalFilter] = useState(null);

  const { mensajeAlerta } = useContext(AlertContext);
  const { state, usuarioAutenticado, resetearPassword } =
    useContext(AuthContext);
  const {
    configuracion,
    usuarios,
    pantallas,
    rows,
    statusAcceso,
    obtenerUsuarios,
    agregarAccesos,
    obtenerAccesos,
    obtenerAcceso,
    setRows,
  } = useContext(ConfiguracionContext);

  const [usuario, guardarUsuario] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [visible, setVisible] = useState(false);
  const [aResetear, setAResetear] = useState(null);
  const toast = useRef(null);
  const navigate = useNavigate();
  const enMovil = useMediaQuery("(max-width:600px)");

  const theme = createBrandTheme(getBrand(configuracion));

  useEffect(() => {
    usuarioAutenticado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (state.usuario) {
      obtenerAcceso({ usuarioId: state.usuario.id, layout: "Accesos" });
    }
    obtenerUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (statusAcceso === 400) {
      mensajeAlerta(
        "Accesos",
        "No tiene permiso para acceder a esta pantalla !.",
        "error",
      );
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusAcceso]);

  const onHide = () => {
    setVisible(false);
    guardarUsuario(null);
    setSelectedRow(null);
  };

  const editClick = () => {
    if (selectedRow) {
      guardarUsuario(selectedRow);
      obtenerAccesos({ usuarioId: selectedRow.id });
      setVisible(true);
    } else {
      toast.current.show({
        severity: "info",
        summary: "Editar",
        detail: "Debe seleccionar un usuario.",
        life: 3000,
      });
    }
  };

  const exitClick = () => navigate("/");

  const aceptarClick = (e) => {
    e.preventDefault();
    if (!usuario || usuario.usuario.trim() === "") {
      mensajeAlerta("Accesos", "El usuario es necesario !.", "error");
      return;
    }
    agregarAccesos({ rows, usuario });
    setVisible(false);
    setSelectedRow(null);
    setTimeout(() => {
      obtenerUsuarios();
    }, 2000);
  };

  const confirmarReset = () => {
    if (aResetear) resetearPassword({ usuarioId: aResetear.id });
    setAResetear(null);
  };

  const boolTemplate = (campo) => (rowData) => (
    <Checkbox checked={!!rowData[campo]} disabled size="small" />
  );

  const accionesTemplate = (rowData) => (
    <Tooltip title="Resetear clave a 123456">
      <IconButton
        size="small"
        color="primary"
        onClick={() => setAResetear(rowData)}
      >
        <RestartAltIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const listado = (
    <Fragment>
      <CabeceraPantalla
        icono={<GroupOutlinedIcon />}
        titulo="Accesos a usuarios"
        subtitulo={usuarios ? `${usuarios.length} usuarios` : "—"}
        onCerrar={exitClick}
        acciones={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems="center"
          >
            <TextField
              size="small"
              placeholder="Buscar…"
              onInput={(e) => setGlobalFilter(e.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 220 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={editClick}
              disabled={!selectedRow}
            >
              Editar
            </Button>
          </Stack>
        }
      />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          "& .p-datatable-wrapper": { overflowX: "auto" },
          "& .p-datatable-thead > tr > th, & .p-datatable-tbody > tr > td": {
            whiteSpace: "nowrap",
          },
        }}
      >
        {usuarios ? (
          <DataTable
            value={usuarios}
            selection={selectedRow}
            onSelectionChange={(e) => setSelectedRow(e.value)}
            selectionMode="single"
            dataKey="id"
            className="p-datatable-sm p-datatable-striped p-datatable-responsive-demo mito-grid"
            globalFilter={globalFilter}
            emptyMessage="No hay datos."
            paginator
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
          >
            <Column field="apellido" header="Apellido" sortable />
            <Column field="nombre" header="Nombre" sortable />
            <Column field="usuario" header="Usuario" sortable />
            <Column field="email" header="Email" sortable />
            <Column
              field="local"
              header="Local"
              body={boolTemplate("local")}
              sortable
            />
            <Column
              field="exterior"
              header="Exterior"
              body={boolTemplate("exterior")}
              sortable
            />
            <Column body={accionesTemplate} header="" style={{ width: "5rem" }} />
          </DataTable>
        ) : (
          <Spinner />
        )}
      </Paper>
    </Fragment>
  );

  return (
    <Fragment>
      <Barra />
      <Toast ref={toast} />

      {statusAcceso === 0 || statusAcceso === 400 ? <Spinner /> : null}

      {statusAcceso === 200 ? (
        <ThemeProvider theme={theme}>
          <Box sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            {listado}

            {/* Diálogo de edición de accesos por pantalla */}
            <Dialog
              open={visible}
              onClose={onHide}
              fullWidth
              maxWidth="md"
              scroll="paper"
              fullScreen={enMovil}
            >
              <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ flexGrow: 1 }}>
                  Accesos{usuario ? ` — ${usuario.usuario}` : ""}
                </Box>
                <IconButton onClick={onHide} size="small">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {usuario ? (
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs={6} sm={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!usuario.local}
                            onChange={(e) =>
                              guardarUsuario({
                                ...usuario,
                                local: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Local"
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!usuario.exterior}
                            onChange={(e) =>
                              guardarUsuario({
                                ...usuario,
                                exterior: e.target.checked,
                              })
                            }
                          />
                        }
                        label="Exterior"
                      />
                    </Grid>
                  </Grid>
                ) : null}

                {usuario && pantallas ? (
                  <Box
                    sx={{
                      "& .p-datatable-wrapper": { overflowX: "auto" },
                      "& .p-datatable-thead > tr > th, & .p-datatable-tbody > tr > td":
                        { whiteSpace: "nowrap" },
                    }}
                  >
                    <DataTable
                      value={pantallas}
                      selection={rows}
                      onSelectionChange={(e) => setRows(e.value)}
                      dataKey="id"
                      className="p-datatable-sm p-datatable-striped"
                      emptyMessage="No hay datos."
                      paginator
                      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                      currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                      rows={10}
                      rowsPerPageOptions={[10, 20, 50]}
                    >
                      <Column
                        selectionMode="multiple"
                        headerStyle={{ width: "3em" }}
                      />
                      <Column field="id" header="ID" sortable />
                      <Column field="menu" header="Menú" sortable />
                      <Column field="descripcion" header="Descripción" sortable />
                    </DataTable>
                  </Box>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={onHide} color="inherit" startIcon={<CloseIcon />}>
                  Cancelar
                </Button>
                <Button
                  onClick={aceptarClick}
                  variant="contained"
                  startIcon={<CheckIcon />}
                >
                  Aceptar
                </Button>
              </DialogActions>
            </Dialog>

            {/* Confirmación de reset de clave */}
            <Dialog open={!!aResetear} onClose={() => setAResetear(null)}>
              <DialogTitle>Confirmación</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  ¿Está seguro de resetear la clave de{" "}
                  <strong>{aResetear ? aResetear.usuario : ""}</strong> a 123456?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAResetear(null)} color="inherit">
                  No
                </Button>
                <Button onClick={confirmarReset} variant="contained" color="error">
                  Sí
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </ThemeProvider>
      ) : null}
    </Fragment>
  );
}
